import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import SearchBar from "../components/SearchBar";
import FilterPanel from "../components/Filters";
import { fetchAllProducts } from "../api/product";
import { seoDefaults } from "../utils/seoDefaults";
import SEO from "../api/seo";

const ProductsPage = ({ title = "Explore Our Shop", categoryFilter }) => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("");
  const [price, setPrice] = useState(5000);
  const [priceFilterActive, setPriceFilterActive] = useState(false);
  const [sort, setSort] = useState("");
  const [loading, setLoading] = useState(true);
  const { categorySlug } = useParams();

  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 10;

  const formattedCategory = categorySlug
    ? categorySlug
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "";

  const dynamicTitle = categorySlug
    ? `Explore Our ${formattedCategory}`
    : title;

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const data = await fetchAllProducts();
        setProducts(data.data);
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const filteredProducts = products
    .filter((product) => {
      const matchesCategoryFromURL = categorySlug
        ? product.category.toLowerCase().replace(/\s+/g, "-") === categorySlug
        : true;
      const matchesSearch = product.name
        .toLowerCase()
        .includes(search.toLowerCase());
      const matchesCategoryFilter = !filter || product.category === filter;
      const withinPrice = priceFilterActive ? product.price <= price : true;
      return (
        matchesCategoryFromURL &&
        matchesSearch &&
        matchesCategoryFilter &&
        withinPrice
      );
    })
    .sort((a, b) => {
      const aIsCategoryMatch = categorySlug
        ? a.category.toLowerCase() === categorySlug
        : false;
      const bIsCategoryMatch = categorySlug
        ? b.category.toLowerCase() === categorySlug
        : false;

      if (aIsCategoryMatch && !bIsCategoryMatch) return -1;
      if (!aIsCategoryMatch && bIsCategoryMatch) return 1;

      if (sort === "low-to-high") return a.price - b.price;
      if (sort === "high-to-low") return b.price - a.price;
      if (sort === "name-asc") return a.name.localeCompare(b.name);
      if (sort === "name-desc") return b.name.localeCompare(a.name);

      return 0;
    });

  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  return (
    <>
      <SEO {...seoDefaults.productList} />
      <section className="min-h-screen px-6 pt-32 pb-20 bg-black text-white relative overflow-hidden">
        {/* Background blur effect */}
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-purple-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/2 right-1/4 w-96 h-96 bg-cyan-900/15 rounded-full blur-3xl animate-pulse" />

        <div className="relative z-10 max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-12">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">
              {dynamicTitle}
            </span>
          </h2>

          <div className="grid md:grid-cols-4 gap-6 mb-10">
            <div className="md:col-span-1">
              <SearchBar searchQuery={search} setSearchQuery={setSearch} />
              <FilterPanel
                categories={["console", "cd", "accessory"]}
                onFilterChange={(e) => setFilter(e.target.value)}
                onPriceChange={(e) => {
                  setPrice(Number(e.target.value));
                  setPriceFilterActive(true);
                }}
                onSortChange={(e) => setSort(e.target.value)}
                selectedSort={sort}
                selectedPrice={price}
              />
            </div>

            <div className="md:col-span-3 space-y-6">
              {loading ? (
                <p className="text-center text-gray-400">Loading products...</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                  {currentProducts.length > 0 ? (
                    currentProducts.map((product) => (
                      <ProductCard key={product._id} product={product} />
                    ))
                  ) : (
                    <p className="text-center col-span-full text-gray-400">
                      No products found.
                    </p>
                  )}
                </div>
              )}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-2 pt-6 flex-wrap">
                  {Array.from({ length: totalPages }, (_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentPage(index + 1)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                        currentPage === index + 1
                          ? "bg-cyan-500 text-white border-cyan-500"
                          : "bg-gray-800 text-gray-300 border-gray-600 hover:bg-cyan-700 hover:text-white"
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductsPage;
